import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { MarketingCard } from '../primitives/MarketingCard';

interface FAQ {
  question: string;
  answer: string;
}

interface ProductFAQsProps {
  faqs: FAQ[];
}

/**
 * Accordion component for individual FAQ items
 */
function Accordion({
  question,
  answer
}: FAQ) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-[#E6ECF2] last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex items-center justify-between text-left hover:text-[#2F7BFF] transition-colors focus:outline-none focus:ring-2 focus:ring-[#2F7BFF]/40 rounded"
        aria-expanded={isOpen}
      >
        <span className="font-semibold text-[#0E1325] pr-4">{question}</span>
        <ChevronDown
          className={`w-5 h-5 text-[#5F6B7A] transition-transform flex-shrink-0 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="pb-4 text-[15px] text-[#5F6B7A] leading-7">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Product FAQs component with accordion functionality
 *
 * @example
 * <ProductFAQs
 *   faqs={[
 *     {
 *       question: 'Is this FCRA compliant?',
 *       answer: 'No. Non-FCRA use cases only.'
 *     }
 *   ]}
 * />
 */
export function ProductFAQs({ faqs }: ProductFAQsProps) {
  return (
    <MarketingCard className="p-4">
      {faqs.map((faq, index) => (
        <Accordion key={index} {...faq} />
      ))}
    </MarketingCard>
  );
}
